import React from 'react';
import { Chip, Stack } from '@mui/material';
import { FeatureIconMap } from '../search/FeatureIconMap';
import type { TrustSignal } from './featureSubDetails';

interface LocationTrustSignalsProps {
  signals: TrustSignal[];
}

/** Read-only trust-signal chips (certifications, bonded, hazmat, security) for a search result card. */
const LocationTrustSignals = ({ signals }: LocationTrustSignalsProps) => {
  if (signals.length === 0) return null;

  return (
    <Stack
      component="ul"
      direction="row"
      flexWrap="wrap"
      // Same 0.7 spacing as the rows of the card's feature list.
      gap={0.7}
      aria-label="Location highlights"
      data-testid="location-trust-signals"
      // Pulled left by the chip's 1px border + MUI's 2px outlined-small icon margin, so the chip icons sit in the
      // feature list's icon column (the pill outline hangs just outside it).
      sx={{ listStyle: 'none', m: 0, p: 0, ml: '-3px' }}
    >
      {signals.map((signal) => {
        const Icon = FeatureIconMap[signal.feature];
        return (
          <Chip
            key={signal.key}
            component="li"
            size="small"
            variant="outlined"
            icon={<Icon htmlColor="#7C8597" />}
            label={signal.label}
            sx={{
              borderColor: '#E9E9FB',
              backgroundColor: '#F5F5FB',
              height: 22,
              // Same size as the feature list's fontSize="small" icons (MUI's chip rule would otherwise force 18px);
              // 1px + the label's 7px padding puts the label at the feature list's 8px icon-to-text gap.
              '& .MuiChip-icon': { fontSize: '1.25rem', mr: '1px' },
              // Set on the label itself: the global CSS reset gives every span 14px. Matches the feature list's text.
              '& .MuiChip-label': { fontSize: '0.857143rem', lineHeight: '1.07143rem' },
            }}
          />
        );
      })}
    </Stack>
  );
};

export default LocationTrustSignals;
